import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import InputText from '@/components/UI/InputText/InputText';
import { SizeVariants } from '@/types';
import CloseButton from '../CloseButton/CloseButton';
import styles from './ConfirmDialog.module.scss';

interface ConfirmDialogProps {
  handleMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  close?: () => void;
  confirm?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  reject?: () => void;
  title?: string;
  message?: string | React.ReactNode;
  children?: React.ReactNode;
  confirmBtnText?: string;
  closeBtnText?: string;
  fullWidth?: boolean;
  maxWidth?: SizeVariants;
  confirmDisabled?: boolean;
  type?: 'root' | 'information';
  noCloseButton?: boolean;
  customButton?: {
    text: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
    disabled?: boolean;
  };
  doubleConfirmationNeeded?: {
    instructions: string;
    expectedInput: string;
  };
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = (props) => {
  const { handleMouseDown, close, confirm, reject, title, message, children, confirmBtnText = 'Confirm', closeBtnText = 'Close', fullWidth, maxWidth,
    confirmDisabled, type = 'root', noCloseButton, customButton, doubleConfirmationNeeded } = props;
  const [userInput, setUserInput] = useState('');
  const isConfirmationButtonDisabled = confirmDisabled || (doubleConfirmationNeeded && userInput !== doubleConfirmationNeeded?.expectedInput);

  // For tests - simplified version without Material-UI styles
  const fullScreen = false;

  // Simplified DialogTitle for tests
  const CustomDialogTitle = ({ children: childrenTitle, onClose, ...other }: React.ComponentProps<typeof DialogTitle> & { onClose?: () => void }) => (
    <DialogTitle {...other} className={styles.DialogTitleContainer}>
      {onClose && (
        <CloseButton onClick={onClose} closeStyle={styles.IconButton} />
      )}
      <div>
        {type === 'information'
          ? <><i className="di icon-information" /> {childrenTitle} </>
          : <span className={styles.DialogTitle}>{childrenTitle}</span>}
      </div>
    </DialogTitle>
  );

  return (
    <Dialog
      id="generic-confirm-dialog"
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      open
      fullScreen={fullScreen}
      aria-labelledby="responsive-dialog-title"
      onMouseDown={handleMouseDown}
    >
      {title && <CustomDialogTitle id="responsive-dialog-title" onClose={close}>{title} </CustomDialogTitle>}

      <DialogContent>
        <div data-testid="dialog-message">{message || ''}</div>
        {!!children && children}

        {doubleConfirmationNeeded && (
          <div className={styles.DoubleConfirmationContainer} data-testid="double-confirmation-message">
            {doubleConfirmationNeeded.instructions}
            <div className={styles.DoubleConfirmationInputContainer} data-testid="double-confirmation-expected-input">
              <InputText
                id="double-confirmation-input"
                value={userInput}
                handleChange={(e) => setUserInput(e.target.value)}
              />
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        {((!!reject || !!close) && !noCloseButton)
          && <Button id="close-reject-btn" data-testid="close-reject-btn" autoFocus={!confirm} onClick={reject || close} color="primary">{closeBtnText}</Button>}
        {confirm
          && <Button id="confirm-btn" data-testid="confirm-btn" variant="contained" disabled={isConfirmationButtonDisabled} onClick={confirm} color="primary" autoFocus={!((!!reject || !!close) && !noCloseButton)}>{confirmBtnText}</Button>}
        {customButton
          && <Button id="custom-btn" data-testid="custom-btn" variant="contained" disabled={customButton.disabled} onClick={customButton.onClick} color="primary">{customButton.text}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
